// Loads the squad's reserves for the Status page.
//
// Each reserve is a Markdown file in src/data/reserves/*.md: a YAML block
// between --- lines, with the description as Markdown below it. If `name`
// matches a reserve in the Lancer data (or a dropped-in LCP), its type and
// description are filled in from there. Exotic gear (a pilot item, mech system
// or weapon from the data, e.g. Shadow of the Wolf's exotics) is matched too and
// listed as type "Exotic". Otherwise the file supplies type and description.
// See _TEMPLATE.md for every field. Files starting with "_" are ignored.

import yaml from "js-yaml";
import { all } from "@/utils/lancerData";
import { lookup } from "@/utils/lancerInfo";

const library = all("reserves");

function findReserve(name) {
	const wanted = String(name || "")
		.trim()
		.toLowerCase();
	return library.find(r => r.name.toLowerCase() === wanted) || null;
}

function parseReserve(raw, file) {
	const text = raw.replace(/\r\n/g, "\n");
	const match = text.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
	let meta;
	try {
		meta = (match && yaml.load(match[1])) || {};
	} catch (err) {
		console.error(`Skipping reserve file ${file}: ${err.message}`);
		return null;
	}
	const name = String(meta.name || "").trim();
	if (!name) return null;

	const known = findReserve(name);
	// Not a reserve: maybe an exotic piece of gear, a system or a weapon
	const exotic = known ? null : lookup.gear(name) || lookup.system(name) || lookup.weapon(name);
	const body = match ? match[2].trim() : "";
	return {
		name: known?.name || exotic?.title || name,
		type: meta.type || known?.type || (exotic ? "Exotic" : "Reserve"),
		count: Number(meta.count) || null,
		// Written description wins; otherwise the library text
		description: body || known?.description || exotic?.body || "",
		callsign: meta.owner ? String(meta.owner).toUpperCase() : "",
		source: known ? "library" : exotic ? "exotic" : "custom",
		file,
	};
}

const files = import.meta.glob("@/data/reserves/*.md", {
	query: "?raw",
	import: "default",
	eager: true,
});

// Ordered by file name, so a numeric prefix (01-..., 02-...) controls the list order
export const reserves = Object.entries(files)
	.filter(([file]) => !file.split("/").pop().startsWith("_"))
	.sort(([a], [b]) => a.localeCompare(b))
	.map(([file, raw]) => parseReserve(raw, file))
	.filter(Boolean);
